import React from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import {
  Flex,
  HStack,
  Text,
  IconButton,
  Icon,
  Box,
  Collapse,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { FiRefreshCw, FiCheck } from "react-icons/fi";
import { GET_JOB_SCRAPING_SESSIONS, TRIGGER_JOB_SCRAPING } from "../graphql/job_scraping";
import { SYNC_JOBS_PATH } from "../routes";

function JobSyncStatus({ onRefresh, currentUser }) {
  const { isOpen } = useDisclosure();
  const navigate = useNavigate();
  const toast = useToast();

  // Mutation for triggering job scraping
  const [triggerJobScrapingMutation, { loading: triggeringLoading }] = useMutation(TRIGGER_JOB_SCRAPING, {
    onCompleted: (data) => {
      if (data.triggerJobScraping) {
        toast({
          title: "Job scraping started",
          description: "Your website is now being scanned for job listings.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        // Navigate to sync jobs page
        navigate(SYNC_JOBS_PATH);
      }
    },
    onError: (error) => {
      toast({
        title: "Failed to start job scraping",
        description: error.message || "An error occurred while starting the scraping process.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  });

  // Query to get the latest job scraping session
  const { data: sessionsData, loading: sessionsLoading } = useQuery(GET_JOB_SCRAPING_SESSIONS, {
    variables: { limit: 1, offset: 0 },
    skip: !currentUser?.agency?.jobWebsite,
  });

  const latestSession = sessionsData?.jobScrapingSessions?.[0];

  const triggerJobScraping = async () => {
    try {
      await triggerJobScrapingMutation();
    } catch (error) {
      console.error('Error triggering job scraping:', error);
    }
  };

  // Don't render if no job website is connected or no sessions exist
  if (!currentUser?.agency?.jobWebsite || !latestSession) {
    return null;
  }

  // Format the completed date
  const formatLastSync = (completedAt) => {
    if (!completedAt) return "Unknown";

    const date = new Date(completedAt);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const sessionDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (sessionDate.getTime() === today.getTime()) {
      return `Today ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Get the sync status message
  const getSyncStatusMessage = () => {
    if (latestSession.status === 'IN_PROGRESS' || latestSession.status === 'PENDING') {
      return "Syncing in progress...";
    }

    const jobsFound = latestSession.totalJobsCreated || 0;
    if (jobsFound === 0) {
      return "No new jobs found";
    } else if (jobsFound === 1) {
      return "1 new job found";
    } else {
      return `${jobsFound} new jobs found`;
    }
  };

  const getLastSyncText = () => {
    if (latestSession.status === 'IN_PROGRESS' || latestSession.status === 'PENDING') {
      return "Sync in progress";
    }
    return `Last synced: ${formatLastSync(latestSession.completedAt)}`;
  };

  return (
    <Box
      bg="teal.800"
      borderRadius="md"
      p={3}
      mb={4}
      border="1px"
      borderColor="teal.600"
    >
      <HStack justify="space-between" align="center">
        <HStack spacing={2} flex={1}>
          <Icon as={FiCheck} color="teal.200" />
          <Text color="teal.200" fontWeight="medium">
            {getSyncStatusMessage()}
          </Text>
        </HStack>

        <HStack spacing={2}>
          <Text color="teal.200" fontSize="sm">
            {getLastSyncText()}
          </Text>
          <IconButton
            icon={<FiRefreshCw />}
            size="sm"
            bg="blackAlpha.900"
            _hover={{ bg: "blackAlpha.600" }}
            border="1px solid blackAlpha.600"
            borderRadius="8px"
            onClick={triggerJobScraping}
            isLoading={sessionsLoading || triggeringLoading}
            aria-label="Re-Sync"
          />
        </HStack>
      </HStack>

      <Collapse in={isOpen} animateOpacity>
        <Box mt={3} pt={3} borderTop="1px" borderColor="teal.600">
          <Flex direction="column" gap={2}>
            <HStack justify="space-between">
              <Text color="teal.200" fontSize="sm">Session Status:</Text>
              <Text color="white" fontSize="sm" fontWeight="medium">
                {latestSession.status}
              </Text>
            </HStack>

            {latestSession.totalJobsFound !== undefined && (
              <HStack justify="space-between">
                <Text color="teal.200" fontSize="sm">Jobs Found:</Text>
                <Text color="white" fontSize="sm">
                  {latestSession.totalJobsFound}
                </Text>
              </HStack>
            )}

            {latestSession.totalJobsCreated !== undefined && (
              <HStack justify="space-between">
                <Text color="teal.200" fontSize="sm">Jobs Created:</Text>
                <Text color="white" fontSize="sm">
                  {latestSession.totalJobsCreated}
                </Text>
              </HStack>
            )}

            {latestSession.totalJobsSkipped !== undefined && latestSession.totalJobsSkipped > 0 && (
              <HStack justify="space-between">
                <Text color="teal.200" fontSize="sm">Jobs Skipped:</Text>
                <Text color="white" fontSize="sm">
                  {latestSession.totalJobsSkipped}
                </Text>
              </HStack>
            )}

            {latestSession.successRate !== undefined && (
              <HStack justify="space-between">
                <Text color="teal.200" fontSize="sm">Success Rate:</Text>
                <Text color="white" fontSize="sm">
                  {(latestSession.successRate * 100).toFixed(2)}%
                </Text>
              </HStack>
            )}

            {latestSession.startedAt && (
              <HStack justify="space-between">
                <Text color="teal.200" fontSize="sm">Started:</Text>
                <Text color="white" fontSize="sm">
                  {new Date(latestSession.startedAt).toLocaleString()}
                </Text>
              </HStack>
            )}
          </Flex>
        </Box>
      </Collapse>
    </Box>
  );
}

export default JobSyncStatus;
